/**
 * Labstep
 *
 * @module prosemirror/components/NodeView/Metadata/Block
 * @desc The element that is rendered when a protocol value is added to the text editor
 */

import classnames from 'classnames';
import { useHasAccess } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { MetadataActionEdit } from 'labstep-web/components/Metadata/Action/Edit';
import { MetadataFormShowEditValue } from 'labstep-web/components/Metadata/Form/ShowEdit/Value';
import { MetadataRequired } from 'labstep-web/components/Metadata/Required';
import { MetadataValue } from 'labstep-web/components/Metadata/Value';
import { ProtocolConditionSmartTableModal } from 'labstep-web/components/ProtocolCondition/SmartTable/Modal';
import { ICONS } from 'labstep-web/constants/icons';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Icon } from 'labstep-web/core/Icon';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { useActiveGroup } from 'labstep-web/hooks/activeGroup';
import {
  ENTITY_VIEW_DEFAULT_PARAMETERS,
  EntityView,
} from 'labstep-web/models/entity-view.model';
import type { Group } from 'labstep-web/models/group.model';
import { getValueFields } from 'labstep-web/models/metadata/utils';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import type React from 'react';
import styles from './styles.module.scss';
import type { MetadataBlockComponentProps } from './types';

export const MetadataBlockComponent: React.FC<
  MetadataBlockComponentProps
> = ({ metadata, entity, readOnly }) => {
  const canEdit = useHasAccess(
    metadata.entityName,
    metadata.id,
    Action.edit,
    getValueFields(metadata),
  );

  const activeGroup = useActiveGroup();

  const defaultEntityView = new EntityView({
    group: activeGroup.activeGroup! as Group,
    context: 'smart_table',
    entity_name: ProtocolCondition.entityName,
    column_definition_ids:
      GridSmartTableProtocolConditionService.getDefaultVariableColIds(
        entity,
      ),
    parameters: ENTITY_VIEW_DEFAULT_PARAMETERS,
  });

  const value = metadata.is_variable ? (
    <ProtocolConditionSmartTableModal
      experimentOrProtocol={entity}
      entityView={defaultEntityView}
      viewComponent={
        <ActionComponent
          type="text"
          icon="external"
          text={`${entity.n_protocol_conditions} ${ProtocolCondition.getHumanReadableEntityName(true)}`}
        />
      }
    />
  ) : (
    <div>
      {!readOnly && canEdit ? (
        <MetadataFormShowEditValue
          entity={entity}
          metadata={metadata}
        />
      ) : (
        <MetadataValue canEdit={canEdit} metadata={metadata} />
      )}
      <MetadataRequired metadata={metadata} />
    </div>
  );

  return (
    <div
      className={classnames(styles.metadataComponentContainer, {
        [styles.fullWidth]:
          ['file', 'sequence', 'molecule'].indexOf(metadata.type) >
            -1 && metadata.hasValue,
      })}
    >
      <div contentEditable={false}>
        {!readOnly && canEdit ? (
          <MetadataActionEdit
            entity={entity}
            metadata={metadata}
            actionComponentProps={{
              type: 'text',
              icon: ICONS.metadata.type[metadata.type],
              text: metadata.label || undefined,
              elementProps: {
                className: styles.metadataLabel,
              },
            }}
          />
        ) : (
          <span className={styles.metadataLabel}>
            <Icon name={ICONS.metadata.type[metadata.type]} />
            {metadata.label}
          </span>
        )}
      </div>
      <div className={styles.metadataValue}>{value}</div>
    </div>
  );
};
